/**
 * Diff Visualizer Tool
 * 
 * Shows side-by-side diff comparison with an editable "compare with" pane.
 */

import { diffLines } from 'diff';
import { ChevronDown, ChevronsUpDown,ChevronUp } from 'lucide-react';
import React from 'react';

import { SearchBar } from '@/components/common/SearchBar';
import { VerticalResizer } from '@/components/common/VerticalResizer';
import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { useUndoableTextarea } from '@/hooks/useUndoableTextarea';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { IVisualizerTool, VisualizerRenderContext } from '@/interfaces/ITool';
import { parseSearchQuery, matchesSearchQuery } from '@/lib/searchQueryParser';
import { cn } from '@/lib/utils';

/** Unit 2.1: Persisted UI state for save/load cycles */
interface DiffPersistedState {
  scrollPosition: { left: number; right: number };
  collapsedSections: number[];
  searchQuery: string;
  whitespaceNormalized: boolean;
  splitRatio: number; // 0.0 to 1.0
}

const DEFAULT_DIFF_STATE: DiffPersistedState = {
  scrollPosition: { left: 0, right: 0 },
  collapsedSections: [],
  searchQuery: '',
  whitespaceNormalized: false,
  splitRatio: 0.5,
};

type DiffRow = {
  left: string;
  right: string;
  kind: 'same' | 'added' | 'removed' | 'changed' | 'empty';
};

/** FR-DIFF-4: A display row is either a real diff row or a collapsed placeholder. */
type DisplayRow =
  | { type: 'row'; row: DiffRow; originalIndex: number }
  | { type: 'collapsed'; startIndex: number; count: number };

function splitLines(text: string): string[] {
  const t = String(text ?? '')
    .replace(/\uFEFF/g, '')       // strip BOM
    .replace(/\r\n/g, '\n')       // normalize Windows line endings
    .replace(/\r/g, '\n')         // normalize lone carriage returns
    .normalize('NFC');             // canonical Unicode normalization
  return t.split('\n');
}

/** Normalize whitespace: collapse runs of spaces/tabs, trim trailing per line */
function normalizeWhitespace(text: string): string {
  return text
    .split('\n')
    .map(line => line.replace(/[ \t]+/g, ' ').trimEnd())
    .join('\n');
}

function buildSideBySideRows(leftText: string, rightText: string): DiffRow[] {
  const changes = diffLines(leftText, rightText, { newlineIsToken: true });

  const rows: DiffRow[] = [];

  for (let i = 0; i < changes.length; i++) {
    const c = changes[i]!;

    if (c.added) {
      for (const line of splitLines(c.value)) {
        rows.push({ left: '', right: line, kind: line === '' ? 'empty' : 'added' });
      }
      continue;
    }

    if (c.removed) {
      const next = changes[i + 1];
      if (next?.added) {
        const leftLines = splitLines(c.value);
        const rightLines = splitLines(next.value);
        const max = Math.max(leftLines.length, rightLines.length);
        for (let j = 0; j < max; j++) {
          const l = leftLines[j] ?? '';
          const r = rightLines[j] ?? '';
          rows.push({ left: l, right: r, kind: l === r ? 'same' : 'changed' });
        }
        i += 1;
        continue;
      }

      for (const line of splitLines(c.value)) {
        rows.push({ left: line, right: '', kind: line === '' ? 'empty' : 'removed' });
      }
      continue;
    }

    for (const line of splitLines(c.value)) {
      rows.push({ left: line, right: line, kind: line === '' ? 'empty' : 'same' });
    }
  }

  while (rows.length > 0 && rows[rows.length - 1]!.left === '' && rows[rows.length - 1]!.right === '') {
    rows.pop();
  }

  return rows;
}

/** FR-DIFF-4: Collapse consecutive unchanged rows, keeping 1 context line above/below each change. */
function buildDisplayRows(
  rows: DiffRow[],
  expandedSections: ReadonlySet<number>,
): DisplayRow[] {
  const CONTEXT_LINES = 1;
  const MIN_COLLAPSE = 4; // only collapse if >3 consecutive unchanged

  // Mark which rows must stay visible (changed rows + context)
  const visible = new Uint8Array(rows.length);
  for (let i = 0; i < rows.length; i++) {
    const k = rows[i]!.kind;
    if (k !== 'same' && k !== 'empty') {
      for (let j = Math.max(0, i - CONTEXT_LINES); j <= Math.min(rows.length - 1, i + CONTEXT_LINES); j++) {
        visible[j] = 1;
      }
    }
  }

  const display: DisplayRow[] = [];
  let i = 0;
  while (i < rows.length) {
    if (visible[i]) {
      display.push({ type: 'row', row: rows[i]!, originalIndex: i });
      i++;
    } else {
      // Start of a collapsible run
      const runStart = i;
      while (i < rows.length && !visible[i]) i++;
      const runCount = i - runStart;

      if (runCount < MIN_COLLAPSE || expandedSections.has(runStart)) {
        // Too short to collapse or user expanded it — show all rows
        for (let j = runStart; j < runStart + runCount; j++) {
          display.push({ type: 'row', row: rows[j]!, originalIndex: j });
        }
      } else {
        display.push({ type: 'collapsed', startIndex: runStart, count: runCount });
      }
    }
  }
  return display;
}

/**
 * Diff view with editable left pane and live diff output.
 */
const DiffView: React.FC<{
  pipelineOutput: string;
  compareWith: string;
  onCompareWithChange: (text: string) => void;
  config?: Record<string, unknown>;
  onConfigChange?: (config: Record<string, unknown>) => void;
}> = ({ pipelineOutput, compareWith, onCompareWithChange, config, onConfigChange }) => {
  const ROW_HEIGHT = 24;
  const OVERSCAN = 10;
  const RESIZER_WIDTH = 4;

  // Unit 2.3/2.4: Restore persisted diff state from config, with graceful defaults
  const restoredState = React.useMemo<DiffPersistedState>(() => {
    const saved = config?._diffState as Partial<DiffPersistedState> | undefined;
    return {
      scrollPosition: {
        left: (saved?.scrollPosition as { left?: number; right?: number })?.left ?? DEFAULT_DIFF_STATE.scrollPosition.left,
        right: (saved?.scrollPosition as { left?: number; right?: number })?.right ?? DEFAULT_DIFF_STATE.scrollPosition.right,
      },
      collapsedSections: saved?.collapsedSections ?? DEFAULT_DIFF_STATE.collapsedSections,
      searchQuery: saved?.searchQuery ?? DEFAULT_DIFF_STATE.searchQuery,
      whitespaceNormalized: saved?.whitespaceNormalized ?? DEFAULT_DIFF_STATE.whitespaceNormalized,
      splitRatio: saved?.splitRatio ?? DEFAULT_DIFF_STATE.splitRatio,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: snapshot persisted config once to seed local state without clobbering live edits
  }, []); // Read once on mount

  // Unit 2.2: Debounced scroll save helper (500ms)
  const scrollSaveTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const latestConfigRef = React.useRef(config);
  latestConfigRef.current = config;

  const saveDiffState = React.useCallback((patch: Partial<DiffPersistedState>, debounce = false) => {
    const doSave = () => {
      const cur = latestConfigRef.current ?? {};
      const prev = (cur._diffState as Partial<DiffPersistedState>) ?? {};
      onConfigChange?.({ ...cur, _diffState: { ...DEFAULT_DIFF_STATE, ...prev, ...patch } });
    };
    if (debounce) {
      clearTimeout(scrollSaveTimerRef.current);
      scrollSaveTimerRef.current = setTimeout(doSave, 500);
    } else {
      doSave();
    }
  }, [onConfigChange]);

  React.useEffect(() => () => clearTimeout(scrollSaveTimerRef.current), []);

  // FR-DIFF-5: Whitespace toggle — initialize from restored state
  const [ignoreWhitespace, setIgnoreWhitespace] = React.useState(restoredState.whitespaceNormalized);

  // Preserve browser undo stack for the compare-with textarea
  const undoableCompare = useUndoableTextarea({ value: compareWith, onChange: onCompareWithChange });

  const effectiveLeft = React.useMemo(
    () => ignoreWhitespace ? normalizeWhitespace(compareWith) : compareWith,
    [compareWith, ignoreWhitespace],
  );
  const effectiveRight = React.useMemo(
    () => ignoreWhitespace ? normalizeWhitespace(pipelineOutput) : pipelineOutput,
    [pipelineOutput, ignoreWhitespace],
  );

  const rows = React.useMemo(
    () => buildSideBySideRows(effectiveLeft, effectiveRight),
    [effectiveLeft, effectiveRight],
  );

  // FR-DIFF-4: Collapse unchanged sections — restore from persisted state
  const [expandedSections, setExpandedSections] = React.useState<Set<number>>(
    () => new Set(restoredState.collapsedSections),
  );

  // Reset collapsed sections when input changes (skip first render to preserve restored state)
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setExpandedSections(new Set());
  }, [effectiveLeft, effectiveRight]);

  const toggleSection = React.useCallback((startIndex: number) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(startIndex)) {
        next.delete(startIndex);
      } else {
        next.add(startIndex);
      }
      saveDiffState({ collapsedSections: [...next] });
      return next;
    });
  }, [saveDiffState]);

  const displayRows = React.useMemo(
    () => buildDisplayRows(rows, expandedSections),
    [rows, expandedSections],
  );

  const added = React.useMemo(() => rows.filter(r => r.kind === 'added').length, [rows]);
  const removed = React.useMemo(() => rows.filter(r => r.kind === 'removed').length, [rows]);
  const changed = React.useMemo(() => rows.filter(r => r.kind === 'changed').length, [rows]);

  // FR-DIFF-2: Hunk navigation
  const hunkPositions = React.useMemo(() => {
    const positions: number[] = [];
    let inHunk = false;
    for (let i = 0; i < rows.length; i++) {
      const isChange = rows[i]!.kind !== 'same' && rows[i]!.kind !== 'empty';
      if (isChange && !inHunk) {
        positions.push(i);
        inHunk = true;
      } else if (!isChange) {
        inHunk = false;
      }
    }
    return positions;
  }, [rows]);

  const [currentHunkIndex, setCurrentHunkIndex] = React.useState(0);

  React.useEffect(() => {
    setCurrentHunkIndex(prev =>
      hunkPositions.length === 0 ? 0 : Math.min(prev, hunkPositions.length - 1)
    );
  }, [hunkPositions]);

  const goToNextHunk = React.useCallback(() => {
    if (hunkPositions.length === 0) return;
    setCurrentHunkIndex(prev => (prev + 1) % hunkPositions.length);
  }, [hunkPositions.length]);

  const goToPrevHunk = React.useCallback(() => {
    if (hunkPositions.length === 0) return;
    setCurrentHunkIndex(prev => (prev - 1 + hunkPositions.length) % hunkPositions.length);
  }, [hunkPositions.length]);

  // Search state — restore from persisted state
  const [searchTerm, setSearchTermRaw] = React.useState(restoredState.searchQuery);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState(restoredState.searchQuery);
  const diffScrollRef = React.useRef<HTMLDivElement>(null);

  const setSearchTerm = React.useCallback((q: string) => {
    setSearchTermRaw(q);
    saveDiffState({ searchQuery: q });
  }, [saveDiffState]);

  // Debounce the search term to avoid lag on large diffs
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 200);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const matchingIndices = React.useMemo(() => {
    if (!debouncedSearchTerm.trim()) return [];
    const query = parseSearchQuery(debouncedSearchTerm);
    return rows.reduce<number[]>((acc, row, i) => {
      if (matchesSearchQuery(row.left, query) || matchesSearchQuery(row.right, query)) {
        acc.push(i);
      }
      return acc;
    }, []);
  }, [rows, debouncedSearchTerm]);

  const [activeMatchIndex, setActiveMatchIndex] = React.useState(-1);
  const totalMatches = matchingIndices.length;

  React.useEffect(() => {
    setActiveMatchIndex(totalMatches > 0 ? 0 : -1);
  }, [debouncedSearchTerm, totalMatches]);

  const goToNext = React.useCallback(() => {
    setActiveMatchIndex(prev => (prev + 1) % totalMatches);
  }, [totalMatches]);

  const goToPrev = React.useCallback(() => {
    setActiveMatchIndex(prev => (prev - 1 + totalMatches) % totalMatches);
  }, [totalMatches]);

  // Map original row indices → display row indices for scroll-to
  const originalToDisplayIndex = React.useMemo(() => {
    const map = new Map<number, number>();
    displayRows.forEach((dr, di) => {
      if (dr.type === 'row') map.set(dr.originalIndex, di);
    });
    return map;
  }, [displayRows]);

  // Virtual scroll to active match
  const activeOriginalIndex = activeMatchIndex >= 0 ? matchingIndices[activeMatchIndex] : -1;
  const activeDisplayIndex = activeOriginalIndex >= 0
    ? (originalToDisplayIndex.get(activeOriginalIndex) ?? -1)
    : -1;

  React.useEffect(() => {
    if (activeDisplayIndex < 0) return;
    const el = diffScrollRef.current;
    if (!el) return;
    const targetTop = activeDisplayIndex * ROW_HEIGHT;
    const viewH = el.clientHeight;
    el.scrollTop = targetTop - viewH / 2 + ROW_HEIGHT / 2;
  }, [activeDisplayIndex]);

  const activeMatchSet = React.useMemo(() => new Set(matchingIndices), [matchingIndices]);

  // Virtual scroll state
  const [scrollTop, setScrollTop] = React.useState(0);
  const [viewHeight, setViewHeight] = React.useState(600);

  React.useEffect(() => {
    const el = diffScrollRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height;
      if (h) setViewHeight(h);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Unit 2.3: Restore scroll position on mount
  React.useEffect(() => {
    const el = diffScrollRef.current;
    if (!el) return;
    if (restoredState.scrollPosition.right > 0) {
      el.scrollTop = restoredState.scrollPosition.right;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: restore scroll position once
  }, []); // Run once on mount

  const handleScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const top = e.currentTarget.scrollTop;
    setScrollTop(top);
    saveDiffState({ scrollPosition: { left: 0, right: top } }, true);
  }, [saveDiffState]);

  // FR-DIFF-3: Resizable split — restore from persisted state
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const [splitRatio, setSplitRatio] = React.useState(restoredState.splitRatio);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const availableWidth = Math.max(0, containerWidth - RESIZER_WIDTH);
  const leftWidth = containerWidth > 0 ? availableWidth * splitRatio : undefined;
  const minWidth = availableWidth * 0.2;
  const maxWidth = availableWidth * 0.8;

  const handleSplitResize = React.useCallback((next: number) => {
    if (containerWidth > 0) {
      const ratio = next / Math.max(1, containerWidth - RESIZER_WIDTH);
      setSplitRatio(ratio);
      saveDiffState({ splitRatio: ratio });
    }
  }, [containerWidth, saveDiffState]);

  const resetSplit = React.useCallback(() => {
    setSplitRatio(0.5);
    saveDiffState({ splitRatio: 0.5 });
  }, [saveDiffState]);

  // FR-DIFF-2: Scroll to current hunk (via display index)
  React.useEffect(() => {
    if (hunkPositions.length === 0) return;
    const rowIdx = hunkPositions[currentHunkIndex];
    if (rowIdx === undefined) return;
    const displayIdx = originalToDisplayIndex.get(rowIdx);
    if (displayIdx === undefined) return;
    const el = diffScrollRef.current;
    if (!el) return;
    const targetTop = displayIdx * ROW_HEIGHT;
    const viewH = el.clientHeight;
    el.scrollTop = targetTop - viewH / 2 + ROW_HEIGHT / 2;
  }, [currentHunkIndex, hunkPositions, originalToDisplayIndex]);

  // FR-DIFF-2: Keyboard shortcuts (Alt+Up/Down)
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 'ArrowDown') {
        e.preventDefault();
        goToNextHunk();
      } else if (e.altKey && e.key === 'ArrowUp') {
        e.preventDefault();
        goToPrevHunk();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goToNextHunk, goToPrevHunk]);

  const totalHeight = displayRows.length * ROW_HEIGHT;
  const startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIdx = Math.min(displayRows.length, Math.ceil((scrollTop + viewHeight) / ROW_HEIGHT) + OVERSCAN);

  return (
    <div className="h-full flex flex-col">
      {/* Stats bar + search + hunk nav + whitespace toggle */}
      <div className="flex items-center gap-3 px-3 h-7 border-b border-border bg-muted/30 shrink-0 text-[11px]">
        <span className="text-diff-added">+{added}</span>
        <span className="text-diff-removed">−{removed}</span>
        {changed > 0 && <span className="text-diff-changed">~{changed}</span>}
        <span className="text-muted-foreground">{rows.length} lines</span>

        {/* FR-DIFF-2: Hunk navigation */}
        {hunkPositions.length > 0 && (
          <div className="flex items-center gap-1">
            <button
              onClick={goToPrevHunk}
              className="p-0.5 rounded hover:bg-muted"
              title="Previous change (Alt+↑)"
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
            <span className="text-muted-foreground tabular-nums">
              Change {currentHunkIndex + 1} of {hunkPositions.length}
            </span>
            <button
              onClick={goToNextHunk}
              className="p-0.5 rounded hover:bg-muted"
              title="Next change (Alt+↓)"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* FR-DIFF-5: Whitespace toggle */}
        <button
          onClick={() => setIgnoreWhitespace(prev => {
            const next = !prev;
            saveDiffState({ whitespaceNormalized: next });
            return next;
          })}
          className={cn(
            'px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border',
            ignoreWhitespace
              ? 'bg-accent text-accent-foreground border-accent'
              : 'bg-transparent text-muted-foreground border-border hover:bg-muted',
          )}
          title={ignoreWhitespace ? 'Whitespace ignored – click to show' : 'Click to ignore whitespace differences'}
        >
          <span className={cn(ignoreWhitespace && 'line-through')}>WS</span>
        </button>

        <div className="ml-auto">
          <SearchBar
            compact
            query={searchTerm}
            onQueryChange={setSearchTerm}
            placeholder="Search diff..."
            resultCount={totalMatches}
            currentIndex={activeMatchIndex}
            onNext={goToNext}
            onPrev={goToPrev}
          />
        </div>
      </div>

      <div ref={containerRef} className="flex-1 flex min-h-0">
        {/* Left: editable compare text */}
        <div
          className="flex flex-col border-r border-border"
          style={leftWidth !== undefined ? { width: leftWidth, flexShrink: 0 } : { width: '50%' }}
        >
          <div className="bg-muted/40 px-3 py-1 border-b border-border text-xs font-semibold text-muted-foreground">
            Compare With
          </div>
          <textarea
            ref={undoableCompare.ref}
            {...undoableCompare.textareaProps}
            placeholder="Paste or type text to compare against…"
            className="flex-1 w-full resize-none bg-background p-3 font-mono text-sm focus:outline-none placeholder:text-muted-foreground/50"
            spellCheck={false}
          />
        </div>

        {/* FR-DIFF-3: Resizable divider */}
        <div onDoubleClick={resetSplit} className="shrink-0 flex">
          <VerticalResizer
            ariaLabel="Resize diff panels"
            min={minWidth}
            max={maxWidth}
            size={leftWidth ?? 0}
            onSizeChange={handleSplitResize}
          />
        </div>

        {/* Right: diff output */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="bg-muted/40 px-3 py-1 border-b border-border text-xs font-semibold text-muted-foreground">
            Pipeline Output
          </div>
          <div ref={diffScrollRef} onScroll={handleScroll} className="flex-1 overflow-auto font-mono text-sm">
            <div className="relative" style={{ height: totalHeight }}>
              {displayRows.slice(startIdx, endIdx).map((displayRow, offset) => {
                const displayIdx = startIdx + offset;

                if (displayRow.type === 'collapsed') {
                  return (
                    <div
                      key={`collapsed-${displayRow.startIndex}`}
                      className="flex items-center justify-center bg-muted/20 border-y border-border/50 cursor-pointer hover:bg-muted/40 transition-colors"
                      style={{ position: 'absolute', top: displayIdx * ROW_HEIGHT, height: ROW_HEIGHT, left: 0, right: 0 }}
                      onClick={() => toggleSection(displayRow.startIndex)}
                    >
                      <ChevronsUpDown className="w-3 h-3 text-muted-foreground mr-1.5" />
                      <span className="text-[10px] text-muted-foreground">
                        Show {displayRow.count} unchanged lines
                      </span>
                    </div>
                  );
                }

                const { row, originalIndex } = displayRow;
                const isMatch = activeMatchSet.has(originalIndex);
                const isActive = originalIndex === activeOriginalIndex;
                const bg =
                  isActive ? 'bg-yellow-500/20 ring-1 ring-inset ring-yellow-500/50' :
                  isMatch ? 'bg-yellow-500/10' :
                  row.kind === 'added' ? 'bg-green-500/10' :
                  row.kind === 'removed' ? 'bg-red-500/10' :
                  row.kind === 'changed' ? 'bg-yellow-500/10' : '';

                return (
                  <div
                    key={originalIndex}
                    className={cn('flex', bg)}
                    style={{ position: 'absolute', top: displayIdx * ROW_HEIGHT, height: ROW_HEIGHT, left: 0, right: 0 }}
                  >
                    <span className="w-10 shrink-0 text-right pr-2 py-0.5 text-muted-foreground/50 select-none text-[10px]">
                      {originalIndex + 1}
                    </span>
                    <span className="flex-1 px-2 py-0.5 truncate">
                      {row.kind === 'removed' ? (
                        <span className="text-diff-removed line-through opacity-60">{row.left || '\u00A0'}</span>
                      ) : row.kind === 'changed' ? (
                        <>
                          <span className="text-diff-removed line-through opacity-60">{row.left}</span>
                          {' → '}
                          <span className="text-diff-added">{row.right}</span>
                        </>
                      ) : (
                        <span className={row.kind === 'added' ? 'text-diff-added' : ''}>
                          {row.right || '\u00A0'}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Diff Visualizer - shows comparison between pipeline output and user-provided text.
 */
class DiffVisualizerToolImpl implements IVisualizerTool {
  readonly id = 'diff-visualizer';
  readonly name = 'Diff Viewer';
  readonly description = 'Compare pipeline output against custom text';
  readonly version = '1.0.0';
  readonly type = 'visualizer' as const;
  readonly category = 'Visualizers';
  readonly editable = false;
  readonly keywords = ['diff', 'compare', 'difference', 'side by side', 'visualizer', 'text', 'merge', 'unified', 'patch'];
  
  readonly supportedInputTypes: ISemanticType[] = [
    SEMANTIC_TYPES.TEXT,
    SEMANTIC_TYPES.JSON,
  ];
  
  readonly outputType = SEMANTIC_TYPES.TEXT;
  
  readonly attributes = [];

  render(context: VisualizerRenderContext): React.ReactElement {
    const { input, config, onConfigChange } = context;
    // FR-DIFF-6: Use comparisonInput as canonical key, fall back to legacy compareWith
    const compareWith = (config.comparisonInput as string)
      ?? (config.compareWith as string)
      ?? '';
    
    const handleCompareWithChange = (text: string) => {
      onConfigChange?.({ ...config, comparisonInput: text, compareWith: text });
    };

    return (
      <DiffView
        pipelineOutput={input}
        compareWith={compareWith}
        onCompareWithChange={handleCompareWithChange}
        config={config}
        onConfigChange={onConfigChange}
      />
    );
  }
  
  toText(input: string): string {
    return input;
  }
}

export const DiffViewerTool = new DiffVisualizerToolImpl();

